import { useState, useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import ComicCard from '@/components/feature/ComicCard';
import { categories, getComicsByCategory } from '@/mocks/comics';
import type { Category } from '@/mocks/comics';

const currentCategory = categories.find((c) => c.slug !== 'all');

const categorySchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  'name': '韩漫分类浏览 - 韩漫漫画网',
  'description': '按分类浏览最新高清免费韩国漫画，涵盖恋爱、奇幻、动作、悬疑、搞笑、耽美、剧情等多个类型。',
  'url': 'https://www.hanmancomic.com/category/',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    const cat = categories.find((c) => c.slug === activeSlug);
    const catName = cat && cat.slug !== 'all' ? cat.name : '全部';
    document.title = `${catName}韩漫 - 最新高清免费韩国漫画分类浏览_韩漫漫画网`;
  }, [activeSlug]);

  const filteredComics = useMemo(() => getComicsByCategory(activeSlug), [activeSlug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) as Category;

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '分类浏览', to: '/category/all' },
    { label: activeCategory?.name || '全部' },
  ];

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(categorySchema)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                {activeCategory?.name || '全部'}韩漫
              </h1>
              {activeCategory && activeCategory.slug !== 'all' && (
                <p className="text-sm text-foreground-500 mt-1">{activeCategory.description}</p>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-8 pb-6 border-b border-background-200/70">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            {filteredComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
                {filteredComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                  <i className="ri-file-search-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类暂无漫画，敬请期待</p>
              </div>
            )}
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}